import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    ButtonGroup,
    Col,
    Container,
    Form,
    Row,
    Table,
} from "react-bootstrap";
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
    useWatch,
} from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";
import Select from "react-select";

import { yupResolver } from "@hookform/resolvers/yup";
import { Elements } from "@stripe/react-stripe-js";
import { StripeElementsOptions, loadStripe } from "@stripe/stripe-js";
import { sanitize } from "dompurify";
import * as yup from "yup";

import { store } from "../..";
import "../../assets/css/majorybl/pages/register.scss";
import { alertActions } from "../../features/alert/alertSlice";
import {
    globalActions,
    selectSetting,
} from "../../features/global/globalSlice";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
    createOrder,
    getClubhouseTeams,
    getCoupon,
    getPaymentIntent,
    getProductCategories,
    getProducts,
} from "../../lib/api";
import { PHONE_REGEXP } from "../../lib/globals";
import { CloseIcon } from "../../lib/icons";
import { displayFormAlert } from "../../lib/utils";
import { logger } from "../../services/logger";
import {
    IAPIResponse,
    ICoupon,
    IProduct,
    IProductCategory,
    IPromoCode,
    ITeam,
} from "../../types/api";
import { IRegisterTeamForm } from "../../types/forms";
import CheckoutForm from "./CheckoutForm";

const stripeKey =
    process.env.BUILD_ENV === "production"
        ? "pk_live_QwrNY8LDtUC2kaUeoZQ6gYLU00tznCwgsy"
        : "pk_test_51S2mfcAhu92Yd58VpZy904e9E7w4j3ckseXHVuHEwHMNpCQhgBYV0GVjrhsKOJR0HqGJc8HfrQ98tlmLiJH9C3ZY00UJddCxwJ";
// Module scope: each loadStripe() call creates a new Stripe instance and iframe,
// so calling it in the component body added one on every render
const stripePromise = loadStripe(stripeKey);

export const loader = async () => {
    const {
        auth,
        global: { settings },
    } = store.getState();
    const promises: Array<Promise<any>> = [
        getProducts(),
        getProductCategories(),
    ];

    if (auth.loggedIn) {
        // In case you're wondering in the future, we call getClubhouseTeams() here
        // even though we're getting all of a users teams and not just their
        // clubhouse (current year's teams) because this is how we get teams by user id
        promises.push(getClubhouseTeams(auth.user!.id, true));
    }

    !settings.length &&
        promises.push(store.dispatch(globalActions.getSettings()));

    const [products, categories, ...rest] = await Promise.all(promises);

    const teams = auth.loggedIn ? rest[0]?.teams || null : null;

    return {
        teams,
        products,
        categories,
    };
};

const RegisterTeam = (): React.JSX.Element => {
    const {
        teams,
        products: { products },
        categories: { categories },
    } = useLoaderData() as {
        teams: ITeam[];
        products: IAPIResponse & {
            products: IProduct[];
        };
        categories: IAPIResponse & {
            categories: IProductCategory[];
        };
    };

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const user = useAppSelector(state => state.auth.user);
    const [cart, setCart] = useState<IRegisterTeamForm[]>([]);
    const [cartTotal, setCartTotal] = useState<number>(0);
    const [cartDiscount, setCartDiscount] = useState<number>(0);
    const [showAddress, setShowAddress] = useState<boolean>(false);
    const [paymentType, setPaymentType] = useState<string>("");
    const [coupon, setCoupon] = useState<ICoupon | undefined>(undefined);
    const [clientSecret, setClientSecret] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    // A ref is the duplicate-call lock because a second click in the same tick
    // reads the stale state value from the previous render's closure.
    const isSubmittingRef = useRef<boolean>(false);

    useEffect(() => {
        if (paymentType === "cc") {
            // Stripe Elements options (including clientSecret) are immutable
            // after mount, so we must unmount and remount with a new secret
            // whenever the cart changes
            setClientSecret(undefined);

            if (cart.length > 0) {
                const timer = setTimeout(() => {
                    getPaymentIntent(cart, cartTotal, cartDiscount)
                        .then(data => {
                            setClientSecret(data.clientSecret);
                        })
                        .catch(e => {
                            logger.error("Error getting payment intent", {
                                error: e,
                                cart,
                                cartTotal,
                                cartDiscount,
                            });
                            dispatch(
                                alertActions.error({
                                    title: `The server encountered a problem initializing the Stripe order`,
                                    text: e,
                                    dismissible: true,
                                }),
                            );
                        });
                }, 300);

                return () => clearTimeout(timer);
            }
        }
    }, [paymentType, cart, cartTotal, cartDiscount]);

    const stripeOptions: StripeElementsOptions = {
        clientSecret,
        appearance: {
            theme: "stripe",
        },
    };

    const termsAndConditions = useAppSelector(state =>
        selectSetting(state, "majorybl_terms_conditions"),
    );

    const registerSchema = yup
        .object()
        .shape({
            firstName: yup.string().required("First Name is required"),
            lastName: yup.string().required("Last Name is required"),
            email: yup
                .string()
                .required("Email address is required")
                .email("Email is invalid"),
            phone: yup
                .string()
                .required("Phone number is required")
                .matches(PHONE_REGEXP, "Phone number is not valid"),
            teamType: yup
                .string()
                .oneOf(
                    ["New", "Returning"],
                    "Please select if you are registering a New or Returning team",
                )
                .required("Please select a new or Returning team"),
            newTeam: yup.string().when("teamType", {
                is: "New",
                then: schema => schema.required("Plase specify your team name"),
                otherwise: schema =>
                    schema
                        .optional()
                        .transform((curr, orig) => (orig === "" ? null : curr)),
            }),
            returningTeam: yup
                .object({
                    value: yup.number(),
                    label: yup.string(),
                })
                .when("teamType", {
                    is: "Returning",
                    then: schema => schema.required("Plase select your team"),
                    otherwise: schema => schema.optional(),
                }),
            league: yup
                .object()
                .required()
                .shape({
                    value: yup.number().required(),
                    label: yup.string().required("League is required"),
                }),
            ageLevel: yup
                .object()
                .required("Age Level is required")
                .shape({
                    value: yup
                        .number()
                        .min(0, "Age Level is required")
                        .required("Age Level is required"),
                    label: yup.string().required("Age Level is required"),
                }),
            promoCode: yup
                .string()
                .optional()
                .transform((curr, orig) => (orig === "" ? null : curr)),
            toc: yup
                .boolean()
                .equals(
                    [true],
                    "You must accept the League's Registration Terms & Conditions",
                ),
            price: yup.number().required(),
        })
        .strict();

    const {
        register,
        handleSubmit,
        control,
        reset,
        resetField,
        setValue,
        setError,
        clearErrors,
        getValues,
        formState: { errors },
    } = useForm<IRegisterTeamForm>({
        mode: "onChange",
        resolver: yupResolver(registerSchema),
        defaultValues: {
            league: undefined,
            firstName: user!.firstName,
            lastName: user!.lastName,
            email: user!.email,
            phone: user!.phone,
            teamType: undefined,
            newTeam: undefined,
            returningTeam: undefined,
            ageLevel: undefined,
            promoCode: "",
        },
    });

    const recalculateCartTotal = () => {
        if (cart.length) {
            let total = cart.reduce((a, c) => {
                return a + c.price;
            }, 0);
            setCartTotal(total);
            setCartDiscount(0);

            if (coupon) {
                switch (coupon.promoCodeType) {
                    case "product":
                        cart.map(p => {
                            if (
                                coupon.appliesTo === null ||
                                (coupon.appliesTo &&
                                    coupon.appliesTo.includes(p.ageLevel.value))
                            ) {
                                switch (coupon.promoCodeCategory) {
                                    case "fixed":
                                        setCartDiscount(
                                            prev =>
                                                prev +
                                                parseInt(
                                                    coupon.discount.toString(),
                                                ),
                                        );
                                        break;
                                    case "percent":
                                        setCartDiscount(
                                            prev =>
                                                prev +
                                                p.price *
                                                    (parseInt(
                                                        coupon.discount.toString(),
                                                    ) /
                                                        100),
                                        );

                                        break;
                                }
                            }

                            return p;
                        });
                        break;
                    case "cart":
                        switch (coupon.promoCodeCategory) {
                            case "fixed":
                                setCartDiscount(
                                    parseInt(coupon.discount.toString()),
                                );
                                break;
                            case "percent":
                                setCartDiscount(
                                    total *
                                        (parseInt(coupon.discount.toString()) /
                                            100),
                                );
                                break;
                        }

                        break;
                }
            }
        } else {
            setCartDiscount(0);
            setCartTotal(0);
        }
    };

    const onSubmit: SubmitHandler<IRegisterTeamForm> = (data, e) => {
        e?.preventDefault();
        setCart(prev => [...prev, data]);
        reset();
        setValue("promoCode", data.promoCode);
    };

    const onError: SubmitErrorHandler<IRegisterTeamForm> = data => {
        console.log(data);
    };

    const submitOrder = async () => {
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        setIsSubmitting(true);

        await createOrder(paymentType, cart, cartTotal, cartDiscount)
            .then(response => {
                // Reset the order state here rather than relying on the
                // component unmounting, which only happens on a page refresh.
                setCart([]);
                setCartTotal(0);
                setCartDiscount(0);
                setCoupon(undefined);
                setPaymentType("");
                setShowAddress(false);
                setClientSecret(undefined);
                setValue("promoCode", "");

                dispatch(
                    alertActions.success({
                        title: `Team(s) registered successfully`,
                        dismissible: true,
                    }),
                );

                // Only leave the page when the API actually returned an order.
                // Without an id there is nothing for the thank you page to
                // load, so the in-place resets and the alert above stand as
                // the complete success behavior.
                if (response?.order?.id) {
                    navigate(`/thankyou?order_id=${response.order.id}`);
                }
            })
            .catch(e => {
                logger.error("Error creating order", {
                    error: e,
                    paymentType,
                    cart,
                    cartTotal,
                    cartDiscount,
                });
                dispatch(
                    alertActions.error({
                        title: `The server encountered a problem registering the team(s).`,
                        text: "If the problem continues, please reach out to an admin.",
                        dismissible: true,
                    }),
                );
            })
            .finally(() => {
                isSubmittingRef.current = false;
                setIsSubmitting(false);
            });
    };

    const removeItem = (idx: number) => {
        setCart(prev => prev.filter((t, itemIdx) => idx !== itemIdx));
        recalculateCartTotal();
    };

    const fetchPromo = async (couponCode: IPromoCode["code"]) => {
        if (
            couponCode.trim() !== "" &&
            (getValues("ageLevel.value") || cart.length)
        ) {
            getCoupon(couponCode)
                .then(response => {
                    if (response && response.coupon) {
                        setCoupon(response.coupon);
                        if (cart.length) {
                            setCart(prev => {
                                const newCart = prev.map(product => {
                                    product.promoCode = response.coupon?.code;
                                    return product;
                                });

                                return newCart;
                            });
                        }
                    }
                })
                .catch(e => {
                    logger.warn("Error fetching promo code", {
                        error: e,
                        couponCode,
                    });
                    setValue("promoCode", "");
                    setError("promoCode", {
                        message: "Invalid promo code",
                    });
                });
        } else {
            setCoupon(undefined);
            setCart(prev => {
                const newCart = prev.map(product => {
                    product.promoCode = "";
                    return product;
                });

                return newCart;
            });
            clearErrors("promoCode");
        }
    };

    useEffect(() => {
        recalculateCartTotal();
    }, [coupon, cart]);

    const teamType = useWatch({
        control,
        name: "teamType",
    });
    const league = useWatch({
        control,
        name: "league",
    });

    const pageText = useAppSelector(state =>
        selectSetting(state, "majorybl_team_registration_content"),
    );

    // Mirrors the API's Utils::calculateCheckDiscount(): the 2% discount is
    // rounded to whole dollars and subtracted from the post-coupon total.
    const finalTotal = Math.round((cartTotal - cartDiscount) * 100) / 100;
    // Any applied coupon, even one that saves $0, excludes the check discount (BBAPI-101).
    const checkDiscountApplies = paymentType === "check" && !coupon;
    // Display only. cartDiscount state stays coupon-only because the API
    // recalculates the check discount itself.
    const checkDiscount = checkDiscountApplies
        ? Math.round(finalTotal * 0.02)
        : 0;
    const displayedDiscount = cartDiscount + checkDiscount;
    const displayedTotal = finalTotal - checkDiscount;

    return (
        <div data-testid="registerContent">
            <Container className="py-5 register">
                <Form
                    onSubmit={handleSubmit(onSubmit, onError)}
                    id="registerForm"
                    data-testid="registerTeamForm"
                >
                    <Row>
                        <Col>
                            <div
                                className="no-bottom-pad"
                                dangerouslySetInnerHTML={{
                                    __html: sanitize(pageText?.setting),
                                }}
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={6} xl={3}>
                            <Form.Group controlId="league">
                                <Form.Label>League</Form.Label>
                                <Controller
                                    name="league"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            value={getValues("league") || null}
                                            options={categories
                                                .filter(c =>
                                                    products.some(
                                                        p =>
                                                            p.category.id ===
                                                            c.id,
                                                    ),
                                                )
                                                .map(c => {
                                                    return {
                                                        value: parseInt(
                                                            c.id.toString(),
                                                        ),
                                                        label: c.categoryName,
                                                    };
                                                })}
                                            onChange={value => {
                                                resetField("ageLevel", {
                                                    defaultValue: {
                                                        value: -1,
                                                        label: "Select...",
                                                    },
                                                });
                                                field.onChange(value);
                                            }}
                                        />
                                    )}
                                />
                                {errors.league &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.league.label?.message,
                                    })}
                            </Form.Group>
                        </Col>
                        <Col sm={6} xl={2}>
                            <Form.Group controlId="ageLevel">
                                <Form.Label>Age Level</Form.Label>
                                <Controller
                                    name="ageLevel"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isSearchable={false}
                                            options={
                                                league
                                                    ? products
                                                          .filter(p => {
                                                              return (
                                                                  p.category
                                                                      .id ===
                                                                      league.value &&
                                                                  p.active ===
                                                                      1 &&
                                                                  (p.startDate
                                                                      ? new Date() >
                                                                        new Date(
                                                                            p.startDate,
                                                                        )
                                                                      : true) &&
                                                                  (p.endDate
                                                                      ? new Date() <
                                                                        new Date(
                                                                            p.endDate,
                                                                        )
                                                                      : true) &&
                                                                  (p.qty !==
                                                                  null
                                                                      ? p.qty >
                                                                        0
                                                                          ? true
                                                                          : false
                                                                      : true)
                                                              );
                                                          })
                                                          .sort((a, b) => {
                                                              const aYear =
                                                                  parseInt(
                                                                      a.meta.year.toString(),
                                                                  );
                                                              const bYear =
                                                                  parseInt(
                                                                      b.meta.year.toString(),
                                                                  );

                                                              if (
                                                                  aYear -
                                                                      bYear !==
                                                                  0
                                                              ) {
                                                                  return (
                                                                      aYear -
                                                                      bYear
                                                                  );
                                                              }

                                                              return (
                                                                  a.category
                                                                      .id -
                                                                  b.category.id
                                                              );
                                                          })
                                                          .map(p => {
                                                              return {
                                                                  value: parseInt(
                                                                      p.id.toString(),
                                                                  ),
                                                                  label: `${p.name} - $${p.price}`,
                                                              };
                                                          })
                                                    : []
                                            }
                                            onChange={value => {
                                                const productPrice = parseFloat(
                                                    products.find(
                                                        p =>
                                                            value!.value ===
                                                            p.id,
                                                    )?.price as string,
                                                );
                                                setValue("price", productPrice);
                                                field.onChange(value);
                                            }}
                                        />
                                    )}
                                />
                                {errors.ageLevel &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.ageLevel.message
                                            ? errors.ageLevel.message
                                            : (errors.ageLevel.value
                                                  ?.message as string),
                                    })}
                            </Form.Group>
                        </Col>
                        <Col xs={8} md={6} xl={3}>
                            <Form.Group controlId="teamType">
                                <Form.Label>
                                    Are you registering a New or Returning Team*
                                </Form.Label>
                                <br />
                                <Form.Control
                                    type="hidden"
                                    {...register("teamType", {
                                        value: undefined,
                                    })}
                                    aria-invalid={
                                        errors.teamType ? "true" : "false"
                                    }
                                />
                                <ButtonGroup>
                                    <Button
                                        variant={
                                            teamType === "New"
                                                ? "primary"
                                                : "outline-primary"
                                        }
                                        onClick={() => {
                                            setValue("teamType", "New", {
                                                shouldValidate: true,
                                            });
                                            resetField("returningTeam");
                                        }}
                                    >
                                        New
                                    </Button>
                                    <Button
                                        variant={
                                            teamType === "Returning"
                                                ? "primary"
                                                : "outline-primary"
                                        }
                                        onClick={() => {
                                            setValue("teamType", "Returning", {
                                                shouldValidate: true,
                                            });
                                            resetField("newTeam");
                                        }}
                                    >
                                        Returning
                                    </Button>
                                </ButtonGroup>
                                {errors.teamType &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.teamType.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6} xl={4}>
                            {teamType === "New" && (
                                <Form.Group as={Col} controlId="newTeam">
                                    <Form.Label>New Team *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        {...register("newTeam")}
                                        aria-invalid={
                                            errors.newTeam ? "true" : "false"
                                        }
                                    />
                                    {errors.newTeam &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.newTeam
                                                .message as string,
                                        })}
                                </Form.Group>
                            )}
                            {teamType === "Returning" && (
                                <Form.Group as={Col} controlId="newTeam">
                                    <Form.Label>Returning Team *</Form.Label>
                                    <Controller
                                        name="returningTeam"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                isSearchable={true}
                                                options={teams
                                                    .filter(
                                                        t =>
                                                            !cart.some(
                                                                i =>
                                                                    i
                                                                        .returningTeam
                                                                        ?.value ===
                                                                    t.id,
                                                            ),
                                                    )
                                                    .map(t => {
                                                        // Get most recent ageLevel for team
                                                        const ageLevel =
                                                            t.ageLevels.reduce(
                                                                (
                                                                    prev,
                                                                    current,
                                                                ) => {
                                                                    return new Date(
                                                                        current.year,
                                                                        current.season.id,
                                                                    ) >
                                                                        new Date(
                                                                            prev.year,
                                                                            prev.season.id,
                                                                        )
                                                                        ? current
                                                                        : prev;
                                                                },
                                                                t.ageLevels[0],
                                                            );

                                                        return {
                                                            value: t.id,
                                                            label: `${t.name} ${ageLevel?.ageLevel} - (${t.coach.firstName} ${t.coach.lastName} - ${t.coach.email})`,
                                                        };
                                                    })}
                                            />
                                        )}
                                    />

                                    {errors.returningTeam &&
                                        displayFormAlert({
                                            level: "danger",
                                            text: errors.returningTeam
                                                .message as string,
                                        })}
                                </Form.Group>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Form.Group as={Col} controlId="firstName">
                            <Form.Label>First Name *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("firstName")}
                                aria-invalid={
                                    errors.firstName ? "true" : "false"
                                }
                            />
                            {errors.firstName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.firstName.message as string,
                                })}
                        </Form.Group>
                        <Form.Group as={Col} controlId="lastName">
                            <Form.Label>Last Name *</Form.Label>
                            <Form.Control
                                type="text"
                                {...register("lastName")}
                                aria-invalid={
                                    errors.lastName ? "true" : "false"
                                }
                            />
                            {errors.lastName &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.lastName.message as string,
                                })}
                        </Form.Group>
                    </Row>
                    <Row>
                        <Form.Group as={Col} md={6} controlId="email">
                            <Form.Label>Email *</Form.Label>
                            <Form.Control
                                type="email"
                                {...register("email")}
                                aria-invalid={errors.email ? "true" : "false"}
                            />
                            {errors.email &&
                                displayFormAlert({
                                    level: "danger",
                                    text: errors.email.message as string,
                                })}
                        </Form.Group>
                        <Col md={6}>
                            <Form.Group controlId="phone">
                                <Form.Label>Phone *</Form.Label>
                                <Form.Control
                                    type="text"
                                    {...register("phone")}
                                    aria-invalid={
                                        errors.phone ? "true" : "false"
                                    }
                                />
                                {errors.phone &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.phone.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group as={Col} controlId="promoCode">
                                <Form.Label>
                                    If you have a Promo code, please apply it
                                    below.
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    {...register("promoCode")}
                                    aria-invalid={
                                        errors.promoCode ? "true" : "false"
                                    }
                                    onBlur={e =>
                                        fetchPromo(e.currentTarget.value)
                                    }
                                />
                                {errors.promoCode &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.promoCode
                                            .message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row className="tandc">
                        <Col>
                            <div
                                className="no-bottom-pad"
                                dangerouslySetInnerHTML={{
                                    __html: sanitize(
                                        termsAndConditions?.setting,
                                    ),
                                }}
                            />
                            <Form.Group controlId="toc">
                                <Form.Check
                                    inline
                                    label="I HAVE READ AND ACCEPTED THE LEAGUE'S
                                    REGISTRATION TEAMS AND CONDITIONS"
                                    type="checkbox"
                                    className="checkbox-xl"
                                    {...register("toc")}
                                    aria-invalid={errors.toc ? "true" : "false"}
                                />
                                {errors.toc &&
                                    displayFormAlert({
                                        level: "danger",
                                        text: errors.toc.message as string,
                                    })}
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Button variant="primary" type="submit">
                                Add to Cart
                            </Button>
                        </Col>
                    </Row>
                </Form>
                <section id="cart">
                    {cart?.length !== 0 && (
                        <>
                            <h4>Your Order</h4>
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Age Level</th>
                                        <th>Team Type</th>
                                        <th>Team Name</th>
                                        <th>Price</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cart.map((c, idx) => {
                                        const product = products.find(
                                            p => p.id === c.ageLevel.value,
                                        );

                                        return (
                                            <tr key={idx}>
                                                <td>
                                                    {`${product?.meta.year} ${product?.category.categoryName}`}
                                                </td>
                                                <td>
                                                    {c.ageLevel.label}
                                                    {coupon &&
                                                    coupon.appliesTo &&
                                                    !coupon?.appliesTo?.includes(
                                                        c.ageLevel.value,
                                                    ) ? (
                                                        <>
                                                            <br />
                                                            <b className="text-danger">
                                                                Promo code does
                                                                not apply to
                                                                this item
                                                            </b>
                                                        </>
                                                    ) : (
                                                        ""
                                                    )}
                                                </td>
                                                <td>{c.teamType}</td>
                                                <td>
                                                    {c.teamType === "New"
                                                        ? c.newTeam
                                                        : c.returningTeam
                                                              ?.label}
                                                </td>
                                                <td>${c.price}</td>
                                                <td>
                                                    <CloseIcon
                                                        className="icon"
                                                        size="xl"
                                                        onClick={() =>
                                                            removeItem(idx)
                                                        }
                                                        style={{
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {displayedDiscount !== 0 && (
                                        <tr id="discount">
                                            <td colSpan={6}>
                                                {cartDiscount > 0 && (
                                                    <h6
                                                        className="mt-3"
                                                        id="cartDiscount"
                                                    >
                                                        Promo Code Discount:{" "}
                                                        {new Intl.NumberFormat(
                                                            "en-US",
                                                            {
                                                                style: "currency",
                                                                currency: "USD",
                                                            },
                                                        ).format(cartDiscount)}
                                                    </h6>
                                                )}
                                                {checkDiscountApplies && (
                                                    <>
                                                        <h6
                                                            className="mt-1"
                                                            id="checkDiscount"
                                                        >
                                                            Check Discount:{" "}
                                                            {new Intl.NumberFormat(
                                                                "en-US",
                                                                {
                                                                    style: "currency",
                                                                    currency:
                                                                        "USD",
                                                                },
                                                            ).format(
                                                                checkDiscount,
                                                            )}
                                                        </h6>
                                                        <p id="checkPrice">
                                                            A 2% discount is
                                                            applied when paying
                                                            by check
                                                        </p>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                    <tr id="subtotal">
                                        <td colSpan={6}>
                                            <h5>
                                                Order Total:{" "}
                                                {new Intl.NumberFormat(
                                                    "en-US",
                                                    {
                                                        style: "currency",
                                                        currency: "USD",
                                                    },
                                                ).format(displayedTotal)}
                                            </h5>
                                        </td>
                                    </tr>
                                    <tr id="paymentOptions">
                                        <td colSpan={6}>
                                            <Form.Check
                                                inline
                                                label="Check payments"
                                                name="paymentType"
                                                type="radio"
                                                value="check"
                                                checked={
                                                    paymentType === "check"
                                                }
                                                id="paymentCheck"
                                                onChange={e => {
                                                    setShowAddress(true);
                                                    setPaymentType(
                                                        e.target.value,
                                                    );
                                                }}
                                            />
                                            <Form.Check
                                                inline
                                                label="Credit Card (Stripe)"
                                                name="paymentType"
                                                type="radio"
                                                value="cc"
                                                id="paymentCC"
                                                checked={paymentType === "cc"}
                                                onChange={e => {
                                                    setShowAddress(false);
                                                    setPaymentType(
                                                        e.target.value,
                                                    );
                                                }}
                                            />
                                            {showAddress &&
                                                paymentType === "check" && (
                                                    <>
                                                        <br />
                                                        <br />
                                                        <p>
                                                            Please send check
                                                            to:
                                                            <br />
                                                            Baseball365
                                                            <br />
                                                            PO Box 4
                                                            <br />
                                                            Janesville, WI 53547
                                                            <br />
                                                            Checks should be
                                                            payable to
                                                            Baseball365
                                                        </p>

                                                        <Button
                                                            variant="primary"
                                                            disabled={
                                                                isSubmitting
                                                            }
                                                            onClick={() =>
                                                                submitOrder()
                                                            }
                                                        >
                                                            {isSubmitting
                                                                ? "Processing"
                                                                : "Submit Order"}
                                                        </Button>
                                                    </>
                                                )}
                                            {paymentType === "cc" &&
                                                clientSecret && (
                                                    <Elements
                                                        stripe={stripePromise}
                                                        options={stripeOptions}
                                                    >
                                                        <CheckoutForm />
                                                    </Elements>
                                                )}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </>
                    )}
                </section>
            </Container>
        </div>
    );
};

export default RegisterTeam;
